import { API_ENDPOINTS } from '../config/api'
import { getActiveOrgId } from '../utils/orgUtils'
import { normalizeComplianceStandardCode } from '../utils/complianceStandard'
import type { ComplianceScopeEntry } from '../utils/complianceScopeDisplay'

type StatusAuthorityPayload = {
  standardCode?: string
  selfAssessment?: { percent?: number | null; source?: string | null }
  verifiedAssessment?: {
    percent?: number | null
    status?: string | null
    source?: string | null
  }
}

function authHeaders(): HeadersInit {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('token') ||
        localStorage.getItem('auth_token') ||
        sessionStorage.getItem('authToken') ||
        localStorage.getItem('accessToken')
      : null
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function toScopeEntry(code: string, auth: StatusAuthorityPayload): ComplianceScopeEntry {
  const confirmed =
    auth.verifiedAssessment?.source === 'evidence_supported_controls' &&
    auth.verifiedAssessment?.percent != null
  return {
    standardCode: code,
    selfAssessedPercent:
      auth.selfAssessment?.percent != null
        ? Math.round(Number(auth.selfAssessment.percent))
        : null,
    verifiedPercent: confirmed ? Math.round(Number(auth.verifiedAssessment!.percent)) : null,
    verifiedStatus: auth.verifiedAssessment?.status ?? null,
    verifiedSource: auth.verifiedAssessment?.source ?? null,
  }
}

/** Org-wide self/verified percents per standard (best-effort parallel). */
export async function fetchOrgComplianceScopeByStandards(
  standardCodes: string[],
): Promise<ComplianceScopeEntry[]> {
  const unique = [
    ...new Set(standardCodes.map((c) => normalizeComplianceStandardCode(c)).filter(Boolean)),
  ]
  if (!unique.length) return []

  const orgId = getActiveOrgId()
  const orgQ = orgId ? `&organization_id=${encodeURIComponent(orgId)}` : ''

  const rows = await Promise.all(
    unique.map(async (code) => {
      try {
        const res = await fetch(
          `${API_ENDPOINTS.COMPLIANCE_STATUS_AUTHORITY}?standardCode=${encodeURIComponent(code)}${orgQ}`,
          { headers: authHeaders() },
        )
        if (!res.ok) return null
        const body = await res.json()
        const auth = (body?.data ?? body) as StatusAuthorityPayload
        return toScopeEntry(code, auth)
      } catch {
        return null
      }
    }),
  )
  return rows.filter((r): r is ComplianceScopeEntry => r != null)
}
